import { DateRangePicker } from '@/components/ui/date-range-picker'
import { MultiSelect } from '@/components/ui/multi-select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ColumnDef } from '@tanstack/react-table'
import { InfoIcon } from 'lucide-react'
import TeamMemberLink, {
  getTeamMemberNameColumnSize,
} from 'app/frontend/base/components/TeamMemberLink'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  usePageInfo,
  useTeamEventStatistics,
} from 'app/frontend/base/config/queries'
import EventStatusIcon, {
  getEventStatusColorClass,
} from 'app/frontend/base/components/EventStatusIcon'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import EventParticipantStatus, {
  eventParticipantStatuses,
} from 'app/frontend/types/custom/EventParticipantStatus'
import {
  defensiveScoreCategories,
  defensiveScoreCategoryLabels,
} from 'app/frontend/types/custom/DefensiveScoreCategory'
import { DateRange } from 'react-day-picker'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import Select from 'app/frontend/base/components/Select'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import SeasonSelect from '../../TeamLoot/components/SeasonSelect'
import AuditFilter, { useAuditFilterState } from '../components/AuditFilter'
import { useAudit } from '../context'
import { ColumnDefWithFilters } from '../types'
import Card from 'app/frontend/base/components/Card'
import { PercentageProgressBar } from '../components/PercentageProgressBar'
import EventInsightsEventsDialog from '../components/EventInsightsEventsDialog'
import { eventRaidReportsSummaryBadge } from '../../column-definitions/events'
import SpellIcon from '../../TeamPlans/components/SpellIcon'
import DefensiveScoreCharacterEntry from 'app/frontend/types/custom/DefensiveScoreCharacterEntry'
import DefensiveScoreSpellEntry from 'app/frontend/types/custom/DefensiveScoreSpellEntry'

type EventInsightsOptionalFilter = 'any' | 'exclude_optional' | 'only_optional'
type ProgressDisplayMode = 'percentage' | 'count'
const HEALTHSTONE_ABILITY_ID = 6262
const HEALING_POTION_ABILITY_ID = 1234768
const DEFENSIVE_SCORE_HEADER_SPELL_ID = 386208

type EventsInsightsRow = {
  key: string
  userReference?: wowaudit.UserReference
  mainCharacterReference: wowaudit.CharacterReference
  altCharacterReferences: wowaudit.CharacterReference[]
  characterMixEntries: Array<{
    character_reference: wowaudit.CharacterReference
    participation_count: number
    participation_share: number
  }>
  statusCounts: Record<EventParticipantStatus, number>
  selectedPercentage: number
  selectedCount: number
  pullsParticipatedPercentage: number
  pullsParticipatedCount: number
  deathsPerPull: number
  deathsCount: number
  healthstonePerPull: number
  healthstoneCount: number
  healingPotionPerPull: number
  healingPotionCount: number
  defensiveScoreOverall: number
  defensiveScoreByCharacter: DefensiveScoreCharacterEntry[]
  mainMembership?: wowaudit.TeamMembership
}

const optionalFilterOptions: {
  value: EventInsightsOptionalFilter
  label: string
}[] = [
  { value: 'any', label: 'Any' },
  { value: 'exclude_optional', label: 'Exclude optional' },
  { value: 'only_optional', label: 'Only optional' },
]

export const useEventsInsightsColumns =
  (): ColumnDefWithFilters<EventsInsightsRow> => {
    const { team } = useTeam()
    const { visibleTeamRanks } = useAudit()
    const {
      data: { currentSeason, currentExpansion, gameVersion },
    } = usePageInfo()
    const { difficulties, defaultDifficulty } = useDifficulties()
    const { value: rangeFrom, setValue: setRangeFrom } =
      useAuditFilterState<string>('eventsInsightsFrom', '')
    const { value: rangeTo, setValue: setRangeTo } =
      useAuditFilterState<string>('eventsInsightsTo', '')
    const isCustomRangeTracked = !!rangeFrom && !!rangeTo
    const { value: seasonId, setValue: setSeasonId } =
      useAuditFilterState<string>(
        'eventsInsightsSeasonId',
        isCustomRangeTracked ? '' : currentSeason.id.toString(),
      )
    const { value: encounterIds, setValue: setEncounterIds } =
      useAuditFilterState<string[]>('eventsInsightsEncounterId', [])
    const { value: difficulty, setValue: setDifficulty } =
      useAuditFilterState<string>(
        'eventsInsightsDifficulty',
        defaultDifficulty,
        difficulties,
      )
    const { value: optionalFilter, setValue: setOptionalFilter } =
      useAuditFilterState<EventInsightsOptionalFilter>(
        'eventsInsightsOptionalFilter',
        'any',
        optionalFilterOptions.map((option) => option.value),
      )
    const { value: progressDisplayMode, setValue: setProgressDisplayMode } =
      useAuditFilterState<ProgressDisplayMode>(
        'eventsInsightsProgressDisplayMode',
        'percentage',
        ['percentage', 'count'],
      )
    const [eventsDialogOpen, setEventsDialogOpen] = useState(false)
    const customRange = useMemo<DateRange | undefined>(() => {
      if (!rangeFrom || !rangeTo) {
        return undefined
      }
      const from = dayjs(rangeFrom)
      const to = dayjs(rangeTo)
      if (!from.isValid() || !to.isValid()) {
        return undefined
      }
      return {
        from: from.toDate(),
        to: to.toDate(),
      }
    }, [rangeFrom, rangeTo])
    const effectiveSeasonId =
      seasonId || (isCustomRangeTracked ? '' : currentSeason.id.toString())

    const selectedSeason = useMemo(
      () =>
        currentExpansion.seasons.find(
          (season) => season.id.toString() === effectiveSeasonId,
        ),
      [currentExpansion.seasons, effectiveSeasonId],
    )
    const encounterOptions = useMemo(() => {
      let sourceInstances = selectedSeason?.instances ?? []
      if (!selectedSeason && isCustomRangeTracked) {
        sourceInstances = currentExpansion.seasons.flatMap(
          (season) => season.instances,
        )
      }

      return sourceInstances
        .flatMap((instance) => instance.encounters)
        .filter(
          (encounter, index, all) =>
            all.findIndex((candidate) => candidate.id === encounter.id) ===
            index,
        )
        .map((encounter) => ({
          value: encounter.id.toString(),
          label: encounter.name,
        }))
    }, [currentExpansion.seasons, isCustomRangeTracked, selectedSeason])
    const query = useMemo(() => {
      const parsedEncounterIds =
        encounterIds.length > 0 ? encounterIds.map(Number) : undefined
      const base = {
        difficulty,
        optionalFilter,
      } as const
      if (customRange?.from && customRange?.to) {
        return {
          ...base,
          from: dayjs(customRange.from).format('YYYY-MM-DD'),
          to: dayjs(customRange.to).format('YYYY-MM-DD'),
          encounterIds: parsedEncounterIds,
        }
      }
      if (effectiveSeasonId) {
        return {
          ...base,
          seasonId: Number(effectiveSeasonId),
          encounterIds: parsedEncounterIds,
        }
      }
      return base
    }, [
      customRange,
      difficulty,
      effectiveSeasonId,
      encounterIds,
      optionalFilter,
    ])
    const { data, isPending } = useTeamEventStatistics(team.id, query)
    const eventsWithRaidReportsCount = data?.eventsWithRaidReportsCount ?? 0
    const totalEventsCount = data?.totalEventsCount ?? 0
    const eventBasicReferences = useMemo(
      () =>
        (data?.eventBasicReferences ?? []).map((event) => ({
          ...event,
          date:
            typeof event.date === 'string'
              ? event.date
              : dayjs(event.date).format('YYYY-MM-DD'),
        })),
      [data?.eventBasicReferences],
    )
    const nameColumnSize = useMemo(
      () => getTeamMemberNameColumnSize(team),
      [team],
    )

    const mainMembershipByCharacterId = useMemo(
      () =>
        Object.fromEntries(
          (team.members ?? []).map((member: wowaudit.TeamMembership) => [
            member.characterReference.id,
            member,
          ]),
        ),
      [team.members],
    )
    const rows = useMemo(() => {
      return (data?.groupedCharacters ?? []).map((groupedCharacter) => {
        const key = String(groupedCharacter.key)
        const performance = data?.performanceByGroupKey?.[key]
        const characterMixEntries = (
          (data?.characterMixByGroupKey?.[key] ??
            []) as unknown as EventsInsightsRow['characterMixEntries']
        ).sort(
          (left, right) => right.participation_share - left.participation_share,
        )
        const statusCountsByKey = (data?.statusCountsByGroupKey[key] ??
          {}) as Partial<Record<EventParticipantStatus, number>>
        const statusCounts = eventParticipantStatuses.reduce(
          (result, status) => ({
            ...result,
            [status]: statusCountsByKey[status] ?? 0,
          }),
          {} as Record<EventParticipantStatus, number>,
        )
        return {
          key,
          userReference: groupedCharacter.userReference,
          mainCharacterReference: groupedCharacter.mainCharacterReference,
          altCharacterReferences: groupedCharacter.altCharacterReferences,
          characterMixEntries,
          statusCounts,
          selectedPercentage: (performance?.selected_percentage ?? 0) * 100,
          selectedCount: performance?.selected_count ?? 0,
          pullsParticipatedPercentage:
            (performance?.pulls_participated_percentage ?? 0) * 100,
          pullsParticipatedCount: performance?.pulls_participated_count ?? 0,
          deathsPerPull: performance?.deaths_per_pull ?? 0,
          deathsCount: performance?.deaths_count ?? 0,
          healthstonePerPull: performance?.healthstone_per_pull ?? 0,
          healthstoneCount: performance?.healthstone_count ?? 0,
          healingPotionPerPull: performance?.healing_potion_per_pull ?? 0,
          healingPotionCount: performance?.healing_potion_count ?? 0,
          defensiveScoreOverall: performance?.defensive_score_overall ?? 0,
          defensiveScoreByCharacter:
            performance?.defensive_score_by_character ?? [],
          mainMembership:
            mainMembershipByCharacterId[
              groupedCharacter.mainCharacterReference.id
            ],
        }
      })
    }, [data, mainMembershipByCharacterId])
    const filteredRows = useMemo(() => {
      if (!visibleTeamRanks.length) {
        return rows
      }
      return rows.filter((row) => {
        const teamRankId = row.mainMembership?.teamRank?.id
        return teamRankId
          ? visibleTeamRanks.includes(teamRankId.toString())
          : false
      })
    }, [rows, visibleTeamRanks])
    const toggleProgressDisplayMode = () =>
      setProgressDisplayMode(
        progressDisplayMode === 'percentage' ? 'count' : 'percentage',
      )

    const columns = useMemo(
      () =>
        [
          {
            id: 'characterReference.name',
            accessorFn: (row) => row.mainCharacterReference.name,
            size: nameColumnSize,
            ...genericSorting<EventsInsightsRow>('text', 'Name'),
            cell: ({ row }) => (
              <div className='flex'>
                <TeamMemberLink
                  member={row.original.mainMembership}
                  characterReference={row.original.mainCharacterReference}
                  iconType='both'
                />
              </div>
            ),
          },

          {
            id: 'availablePercentage',
            accessorFn: (row) => {
              const totalCount = eventParticipantStatuses.reduce(
                (total, status) => total + (row.statusCounts[status] ?? 0),
                0,
              )
              if (totalCount === 0) return 0
              const availableCount =
                (row.statusCounts.present ?? 0) +
                (row.statusCounts.standby ?? 0)
              return availableCount / totalCount
            },
            meta: { className: 'text-left border-r' },
            ...genericSorting<EventsInsightsRow>(
              'basic',
              <span className='inline-flex items-center gap-1.5'>
                <span>Available</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='inline-flex'>
                      <InfoIcon className='w-3.5 h-3.5 text-muted-foreground' />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>
                      The present and standby statuses count as available
                    </span>
                  </TooltipContent>
                </Tooltip>
              </span>,
              true,
            ),
            cell: ({ row }) => {
              const totalCount = eventParticipantStatuses.reduce(
                (total, status) =>
                  total + (row.original.statusCounts[status] ?? 0),
                0,
              )
              const availableCount =
                (row.original.statusCounts.present ?? 0) +
                (row.original.statusCounts.standby ?? 0)
              const percentage =
                totalCount === 0 ? 0 : (availableCount / totalCount) * 100
              return (
                <PercentageProgressBar
                  percentage={percentage}
                  countLabel={availableCount}
                  displayMode={progressDisplayMode}
                  onToggleDisplayMode={toggleProgressDisplayMode}
                />
              )
            },
          },
          {
            id: 'statusCounts',
            header: <span className='whitespace-nowrap'>Status count</span>,
            meta: { className: 'border-r' },
            columns: eventParticipantStatuses
              .filter((status) =>
                filteredRows.some((row) => (row.statusCounts[status] ?? 0) > 0),
              )
              .map((status, index, statuses) => ({
                id: `statusCount_${status}`,
                size: 45,
                minSize: 45,
                maxSize: 45,
                meta: {
                  className:
                    index === statuses.length - 1 ? 'border-r' : undefined,
                },
                accessorFn: (row: EventsInsightsRow) =>
                  row.statusCounts[status] ?? 0,
                ...genericSorting<EventsInsightsRow>(
                  'basic',
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className='inline-flex'>
                        <EventStatusIcon status={status} />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <span className={`capitalize`}>{status}</span>
                    </TooltipContent>
                  </Tooltip>,
                  true,
                ),
                cell: ({ row }: { row: { original: EventsInsightsRow } }) => {
                  const count = row.original.statusCounts[status] ?? 0
                  return count > 0 ? (
                    <span className={getEventStatusColorClass(status)}>
                      {count}
                    </span>
                  ) : (
                    ''
                  )
                },
              })),
          },
          {
            id: 'selectedPercentage',
            accessorFn: (row) => row.selectedPercentage / 100,
            meta: { className: 'text-left border-r' },
            ...genericSorting<EventsInsightsRow>(
              'basic',
              <span className='inline-flex items-center gap-1.5'>
                <span>Selected</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='inline-flex'>
                      <InfoIcon className='w-3.5 h-3.5 text-muted-foreground' />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>
                      How often the user is selected for encounters, instead of
                      being benched (when available)
                    </span>
                  </TooltipContent>
                </Tooltip>
              </span>,
              true,
            ),
            cell: ({ row }) => (
              <PercentageProgressBar
                percentage={row.original.selectedPercentage}
                countLabel={row.original.selectedCount}
                displayMode={progressDisplayMode}
                onToggleDisplayMode={toggleProgressDisplayMode}
              />
            ),
          },
          {
            id: 'warcraftLogs',
            meta: { className: 'border-r' },
            header: (
              <span className='inline-flex items-center gap-1.5 whitespace-nowrap'>
                <img
                  src='https://data.wowaudit.com/img/wcl-logo.png'
                  alt='Warcraft Logs'
                  className='w-4 h-4'
                />
                <span>Warcraft Logs</span>
                {eventRaidReportsSummaryBadge({
                  linkedCount: eventsWithRaidReportsCount,
                  totalCount: totalEventsCount,
                  onClick: () => setEventsDialogOpen(true),
                })}
              </span>
            ),
            columns: [
              {
                id: 'pullsParticipatedPercentage',
                accessorFn: (row) => row.pullsParticipatedPercentage / 100,
                meta: { className: 'text-left' },
                ...genericSorting<EventsInsightsRow>(
                  'basic',
                  <span className='inline-flex items-center gap-1.5'>
                    <span>Participated</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className='inline-flex'>
                          <InfoIcon className='w-3.5 h-3.5 text-muted-foreground' />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <span>
                          Percentage of actual pulls the player participated in
                          (when available)
                        </span>
                      </TooltipContent>
                    </Tooltip>
                  </span>,
                  true,
                ),
                cell: ({ row }) => (
                  <PercentageProgressBar
                    percentage={row.original.pullsParticipatedPercentage}
                    countLabel={row.original.pullsParticipatedCount}
                    displayMode={progressDisplayMode}
                    onToggleDisplayMode={toggleProgressDisplayMode}
                  />
                ),
              },
              {
                id: 'deathsPerPull',
                accessorFn: (row) => row.deathsPerPull,
                meta: {
                  className: gameVersion === 'live' ? '' : 'border-r',
                },
                ...genericSorting<EventsInsightsRow>(
                  'basic',
                  <span className='inline-flex items-center gap-1.5'>
                    <span>Died</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className='inline-flex'>
                          <InfoIcon className='w-3.5 h-3.5 text-muted-foreground' />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <span>
                          How often the player died early (in first 4, excluding
                          clustered deaths)
                        </span>
                      </TooltipContent>
                    </Tooltip>
                  </span>,
                  true,
                ),
                cell: ({ row }) => {
                  if (row.original.pullsParticipatedCount === 0) return null

                  return progressDisplayMode === 'count'
                    ? row.original.deathsCount
                    : Math.round(row.original.deathsPerPull * 100).toFixed(0) +
                        '%'
                },
              },
              ...(gameVersion === 'live'
                ? [
                    {
                      id: 'defensiveScoreOverall',
                      accessorFn: (row: EventsInsightsRow) =>
                        row.defensiveScoreOverall,
                      meta: { className: 'text-center' },
                      ...genericSorting<EventsInsightsRow>(
                        'basic',
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='inline-flex'>
                              <SpellIcon
                                spellId={DEFENSIVE_SCORE_HEADER_SPELL_ID}
                                size='medium'
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className='flex flex-col gap-1 items-center'>
                            <div>Defensive usage score across all pulls</div>
                            <div className='text-xs text-muted-foreground max-w-92 text-center'>
                              A score of 100 means defensives were always used
                              on cooldown, so keep in mind that a higher score
                              isn&apos;t necessarily better
                            </div>
                          </TooltipContent>
                        </Tooltip>,
                        true,
                      ),
                      cell: ({
                        row,
                      }: {
                        row: { original: EventsInsightsRow }
                      }) => {
                        if (row.original.pullsParticipatedCount === 0)
                          return null
                        const roundedDefensiveScore = Math.round(
                          row.original.defensiveScoreOverall * 100,
                        )

                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className='inline-flex cursor-default'>
                                {roundedDefensiveScore}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className='flex flex-col gap-2'>
                                {(
                                  row.original.defensiveScoreByCharacter ?? []
                                ).map(
                                  (
                                    characterEntry: DefensiveScoreCharacterEntry,
                                  ) => (
                                    <div
                                      key={characterEntry.character_id}
                                      className='flex flex-col gap-2'
                                    >
                                      {defensiveScoreCategories
                                        .filter(
                                          (category) =>
                                            (
                                              characterEntry.categories[
                                                category
                                              ] ?? []
                                            ).length > 0,
                                        )
                                        .map((category) => (
                                          <div
                                            key={`${characterEntry.character_id}_${category}`}
                                            className='flex flex-col gap-1'
                                          >
                                            <div className='border-b text-xs'>
                                              {
                                                defensiveScoreCategoryLabels[
                                                  category
                                                ]
                                              }
                                            </div>
                                            {(
                                              characterEntry.categories[
                                                category
                                              ] ?? []
                                            ).map(
                                              (
                                                spell: DefensiveScoreSpellEntry,
                                              ) => (
                                                <div
                                                  key={`${characterEntry.character_id}_${category}_${spell.spell_id}`}
                                                  className='flex items-center justify-between gap-4'
                                                >
                                                  <a
                                                    href={`https://wowhead.com/spell=${spell.spell_id}`}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    className='flex items-center gap-1.5'
                                                  >
                                                    <SpellIcon
                                                      spellId={spell.spell_id}
                                                      size='small'
                                                    />
                                                    <span>{spell.name}</span>
                                                  </a>
                                                  <span className='font-semibold'>
                                                    {Math.round(
                                                      spell.score * 100,
                                                    )}
                                                    %
                                                  </span>
                                                </div>
                                              ),
                                            )}
                                          </div>
                                        ))}
                                    </div>
                                  ),
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )
                      },
                    },
                    {
                      id: 'healthstonePerPull',
                      accessorFn: (row: EventsInsightsRow) =>
                        row.healthstonePerPull,
                      meta: { className: 'text-center' },
                      ...genericSorting<EventsInsightsRow>(
                        'basic',
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='inline-flex'>
                              <SpellIcon
                                spellId={HEALTHSTONE_ABILITY_ID}
                                size='medium'
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span>Healthstones used per pull</span>
                          </TooltipContent>
                        </Tooltip>,
                        true,
                      ),
                      cell: ({
                        row,
                      }: {
                        row: { original: EventsInsightsRow }
                      }) => {
                        if (row.original.pullsParticipatedCount === 0)
                          return null

                        return (
                          Math.round(row.original.healthstonePerPull * 100) /
                          100
                        )
                      },
                    },
                    {
                      id: 'healingPotionPerPull',
                      accessorFn: (row: EventsInsightsRow) =>
                        row.healingPotionPerPull,
                      meta: { className: 'text-center border-r' },
                      ...genericSorting<EventsInsightsRow>(
                        'basic',
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='inline-flex'>
                              <SpellIcon
                                spellId={HEALING_POTION_ABILITY_ID}
                                size='medium'
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span>Healing potions used per pull</span>
                          </TooltipContent>
                        </Tooltip>,
                        true,
                      ),
                      cell: ({
                        row,
                      }: {
                        row: { original: EventsInsightsRow }
                      }) => {
                        if (row.original.pullsParticipatedCount === 0)
                          return null

                        return (
                          Math.round(row.original.healingPotionPerPull * 100) /
                          100
                        )
                      },
                    },
                  ]
                : []),
            ],
          },
          {
            id: 'characterMix',
            accessorFn: (row) =>
              row.characterMixEntries
                .filter(
                  (entry) =>
                    entry.character_reference.id !==
                    row.mainCharacterReference.id,
                )
                .reduce(
                  (highest, entry) =>
                    Math.max(highest, entry.participation_share),
                  0,
                ),
            meta: { className: 'text-left' },
            ...genericSorting<EventsInsightsRow>('basic', 'Alts'),
            cell: ({ row }) => (
              <div className='flex items-center justify-start gap-2 flex-wrap'>
                {row.original.characterMixEntries
                  .filter(
                    (entry) =>
                      entry.character_reference.id !==
                      row.original.mainCharacterReference.id,
                  )
                  .map((entry) => {
                    const percentage =
                      Math.round(entry.participation_share * 1000) / 10
                    const isWholePercentage = Number.isInteger(percentage)
                    const percentageLabel = isWholePercentage
                      ? percentage.toFixed(0)
                      : percentage.toFixed(1)

                    return (
                      <Tooltip key={entry.character_reference.id}>
                        <TooltipTrigger asChild>
                          <Card className='flex items-center gap-1.5 p-0 pr-1'>
                            {entry.character_reference.currentSpec?.icon ? (
                              <BlizzardIcon
                                iconName={
                                  entry.character_reference.currentSpec.icon
                                }
                                className='border-none'
                                size='medium'
                              />
                            ) : (
                              <CharacterClassIcon
                                characterClass={
                                  entry.character_reference.characterClass
                                }
                                className='border-none rounded-r-none'
                                size='medium'
                              />
                            )}
                            <span className='text-xs'>{percentageLabel}%</span>
                          </Card>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className='flex items-center gap-1.5 justify-center'>
                            <CharacterClassIcon
                              characterClass={
                                entry.character_reference.characterClass
                              }
                              size='small'
                              style='blank'
                            />
                            <span
                              className={
                                classColours[
                                  entry.character_reference.characterClass
                                ].text
                              }
                            >
                              {entry.character_reference.name}
                            </span>
                          </div>
                          <div className='text-xs text-muted-foreground text-center'>
                            {entry.character_reference.currentSpec?.name
                              ? `${entry.character_reference.currentSpec.name} ${entry.character_reference.characterClass}`
                              : entry.character_reference.characterClass}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    )
                  })}
              </div>
            ),
          },
        ] as ColumnDef<EventsInsightsRow>[],
      [
        eventsWithRaidReportsCount,
        filteredRows,
        nameColumnSize,
        progressDisplayMode,
        toggleProgressDisplayMode,
        totalEventsCount,
      ],
    )

    const filters = useMemo(
      () => [
        <AuditFilter key='dateRange' label='Date range'>
          <DateRangePicker
            date={customRange}
            setDate={(range) => {
              if (range?.from && range?.to) {
                setRangeFrom(dayjs(range.from).format('YYYY-MM-DD'))
                setRangeTo(dayjs(range.to).format('YYYY-MM-DD'))
                setSeasonId('')
              } else {
                setRangeFrom('')
                setRangeTo('')
              }
              if (!range && !seasonId) {
                setSeasonId(currentSeason.id.toString())
              }
            }}
            isClearable={true}
          />
        </AuditFilter>,
        <AuditFilter key='season' label='Season'>
          <SeasonSelect
            className='w-52'
            value={effectiveSeasonId || undefined}
            setValue={(value) => {
              setSeasonId(value ?? '')
              setEncounterIds([])
              if (value) {
                setRangeFrom('')
                setRangeTo('')
              }
            }}
          />
        </AuditFilter>,
        <AuditFilter key='encounter' label='Encounter'>
          <MultiSelect<string>
            className='w-52'
            placeholder='All encounters'
            value={encounterIds}
            onChange={setEncounterIds}
            options={encounterOptions}
            selectedDisplay='count'
          />
        </AuditFilter>,
        ...(difficulties.length > 1
          ? [
              <AuditFilter key='difficulty' label='Primary difficulty'>
                <Select
                  className='w-32'
                  setValue={(value) =>
                    setDifficulty((value as string) ?? defaultDifficulty)
                  }
                  value={difficulty}
                  options={difficulties.map((diff) => ({
                    label: difficultiesLabels[diff],
                    value: diff,
                  }))}
                />
              </AuditFilter>,
            ]
          : []),
        <AuditFilter key='optional' label='Optional'>
          <Select
            className='w-44'
            value={optionalFilter}
            setValue={(value) =>
              setOptionalFilter((value as EventInsightsOptionalFilter) ?? 'any')
            }
            options={optionalFilterOptions}
          />
        </AuditFilter>,
        <EventInsightsEventsDialog
          key='events-dialog'
          open={eventsDialogOpen}
          onOpenChange={setEventsDialogOpen}
          teamId={team.id}
          events={eventBasicReferences}
          isLoading={isPending}
        />,
      ],
      [
        currentSeason.id,
        customRange,
        eventBasicReferences,
        effectiveSeasonId,
        encounterIds,
        encounterOptions,
        eventsDialogOpen,
        difficulty,
        isPending,
        optionalFilter,
        setDifficulty,
        team.id,
        setEncounterIds,
        setOptionalFilter,
        setRangeFrom,
        setRangeTo,
        setSeasonId,
      ],
    )

    return {
      columns,
      data: filteredRows,
      filters,
      initialSortBy: [{ id: 'characterReference.name', desc: false }],
      showDefaultMemberColumn: false,
      showPendingDataMessage: false,
      emptyMessage: 'No event insights found for the selected filters.',
      loading: isPending,
    }
  }
